import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { KeenIcon, LazyFormikSelect } from '@/components';
import { buildBulkEditPayload } from './bulkEditPayload';

/**
 * One small form applied to every selected row (OL-138). Every field starts
 * blank and a blank field means "leave as is", so the modal only sends what
 * the user actually picked.
 *
 * fields: [{ name, label, options }] for a plain select (add `boolean: true`
 * for a yes/no select), or [{ name, label, loadOptions, isMulti }] for a
 * lazily loaded select. `disabled` and `hint` grey a field out and explain why.
 */
const BulkEditModal = ({ open, title, selectedCount, fields, loading, onSubmit, onClose }) => {
  const [error, setError] = useState('');
  if (!open) return null;

  const initialValues = Object.fromEntries(fields.map((field) => [field.name, field.isMulti ? [] : '']));
  const booleanFields = fields.filter((field) => field.boolean).map((field) => field.name);

  const handleSubmit = (values) => {
    const payload = buildBulkEditPayload(values, booleanFields);
    if (Object.keys(payload).length === 0) {
      setError('Choose at least one field to change.');
      return;
    }
    setError('');
    onSubmit(payload);
  };

  return (
    <div className="modal-wrapper modal-animate-fade">
      <div className="modal-body w-4/5 max-w-xl animate-scale">
        <h3 className="card-title">{title}</h3>
        <p className="mt-1 text-sm text-gray-600">
          Applies to {selectedCount} selected. Fields left blank are not changed.
        </p>
        <button type="button" onClick={onClose} className="modal-close-btn">
          <KeenIcon icon="cross" />
        </button>

        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          <Form className="mt-3">
            {fields.map((field) => (
              <div key={field.name} className="mb-3">
                <label className="mb-1 form-label">{field.label}</label>
                {field.loadOptions ? (
                  <LazyFormikSelect
                    name={field.name}
                    loadOptions={field.loadOptions}
                    pageSize={20}
                    isMulti={!!field.isMulti}
                    placeholder="Leave unchanged"
                  />
                ) : (
                  <Field as="select" name={field.name} className="input" disabled={!!field.disabled}>
                    <option value="">Leave unchanged</option>
                    {field.options.map((option) => (
                      <option key={String(option.value)} value={option.value}>{option.label}</option>
                    ))}
                  </Field>
                )}
                {field.hint && <p className="mt-1 text-xs text-gray-500">{field.hint}</p>}
              </div>
            ))}
            {error && <div className="mb-2 text-xs text-red-500">{error}</div>}

            <div className="flex items-center justify-end gap-2 mt-4">
              <button type="submit" disabled={loading} className="btn btn-sm btn-primary">
                {loading ? 'Please Wait...' : `Update ${selectedCount}`}
              </button>
              <button type="button" onClick={onClose} className="btn btn-sm btn-outline btn-danger">
                Cancel
              </button>
            </div>
          </Form>
        </Formik>
      </div>
    </div>
  );
};

const YES_NO_OPTIONS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

export { BulkEditModal, YES_NO_OPTIONS };
